import { useEffect, useRef, useState } from 'react';
import type { Dispatch } from 'react';
import type { AccountScope, AppAction } from '../contexts/AppContext';
import type { Account, AuthContext, Broker, OffboardingStep, Position } from '../types';
import type { TokenInfo } from '../services/orderly-api';
import type { Eip712Signer } from '../services/wallet/eip712.ts';
import type { SolanaMessageSigner } from '../services/wallet/solana.ts';
import {
  executeCancelOrders,
  executeConvertToUsdc,
  executeClosePositions,
  executeInternalTransfer,
  executeSettlePnl,
  executeWithdrawOrTransfer,
  OffboardingOperationAbortedError,
} from '../services/offboarding';
import type { ConvertToUsdcResult } from '../services/offboarding';
import { formatFullWithdrawalAmount, getWalletWithdrawalAmountError, getWithdrawalErrorMessage, isSubAtomicWithdrawalRemainder, isValidTokenDecimals, isWithdrawalAmountAboveAvailable, parseAtomicWithdrawalAmount } from '../lib/withdrawal';
import { trackEvent } from '../lib/analytics';
import { captureException } from '../sentry';
import { createAccountDataRequestBarrier } from './useAccountData';

interface Callbacks {
  onProgress?: (message: string) => void;
  shouldContinue?: () => boolean;
}

interface Actions {
  cancelOrders: (auth: AuthContext, accountId: string, cb?: Callbacks) => Promise<void>;
  closePositions: (positions: Position[], auth: AuthContext, cb?: Callbacks) => Promise<void>;
  settlePnl: (accountType: 'main' | 'sub', accountId: string, brokerId: string, auth: AuthContext, signer: Eip712Signer | SolanaMessageSigner, chainId: number, chainType: 'evm' | 'solana', cb?: Callbacks) => Promise<void>;
  convertToUsdc: (accountId: string, plan: Array<{ token: string; amount: number }>, auth: AuthContext, cb?: Callbacks, slippage?: number) => Promise<ConvertToUsdcResult>;
  withdrawOrTransfer: (
    account: Account,
    token: string,
    rawAmount: string,
    mainAccountId: string,
    brokerId: string,
    auth: AuthContext,
    signer: Eip712Signer | SolanaMessageSigner | undefined,
    chainId: number | undefined,
    chainType: 'evm' | 'solana' | undefined,
    destinationChainId: number | undefined,
    receiverAddress: string | undefined,
    cb: Callbacks | undefined,
    tokenDecimals: number | undefined,
  ) => Promise<void>;
  internalTransfer: (fromAccountId: string, toAccountId: string, token: string, amount: number, auth: AuthContext, cb?: Callbacks) => Promise<void>;
}

export interface ExecuteStepInput {
  step: OffboardingStep;
  withdrawToken: string;
  withdrawAction: 'wallet' | 'transfer';
  selectedDestinationChain: number | null;
  transferTargetAccountId: string;
  withdrawAmountRaw?: string;
  withdrawAmountSource?: 'auto' | 'manual';
  convertPlan?: Array<{ token: string; amount: number }>;
  convertSlippage?: number;
}

export type ExecuteStepResult = 'success' | 'partial' | 'unsettled' | 'error' | 'rejected';

interface UseOffboardingActionsOptions {
  account: Account | null;
  broker: Broker | null;
  auth: AuthContext | null;
  sessionGeneration: number;
  dispatch: Dispatch<AppAction>;
  signer?: Eip712Signer | SolanaMessageSigner;
  chainId?: number;
  chainType?: 'evm' | 'solana';
  withdrawalReceiverAddress?: string | null;
  tokenInfoMap: Map<string, TokenInfo>;
  withdrawalAvailableMap?: Map<string, number>;
  onStepComplete?: () => void;
  actions?: Partial<Actions>;
}

const defaultActions: Actions = {
  cancelOrders: executeCancelOrders,
  closePositions: executeClosePositions,
  settlePnl: executeSettlePnl,
  convertToUsdc: executeConvertToUsdc,
  withdrawOrTransfer: executeWithdrawOrTransfer,
  internalTransfer: executeInternalTransfer,
};

const SOLANA_CHAIN_ID = 900900900;

export function useOffboardingActions({
  account,
  broker,
  auth,
  sessionGeneration,
  dispatch,
  signer,
  chainId,
  chainType,
  withdrawalReceiverAddress,
  tokenInfoMap,
  withdrawalAvailableMap,
  onStepComplete,
  actions,
}: UseOffboardingActionsOptions) {
  const [loadingExecution, setLoadingExecution] = useState<(AccountScope & { step: OffboardingStep; sequence: number }) | null>(null);
  const executionSequence = useRef(0);
  const activeOperations = useRef(new Map<string, { sequence: number; sessionGeneration: number; controller: AbortController }>());
  const mergedActions = { ...defaultActions, ...actions };

  useEffect(() => {
    const generation = sessionGeneration;
    const operations = activeOperations.current;
    return () => {
      for (const operation of operations.values()) {
        if (operation.sessionGeneration === generation) operation.controller.abort();
      }
    };
  }, [sessionGeneration]);

  async function executeStep(input: ExecuteStepInput): Promise<ExecuteStepResult> {
    if (!account || !broker) {
      return 'error';
    }

    const origin: AccountScope = {
      sessionGeneration,
      brokerId: broker.id,
      accountId: account.accountId,
    };
    const operationKey = JSON.stringify([origin.sessionGeneration, origin.brokerId, origin.accountId, input.step]);
    if (activeOperations.current.has(operationKey)) {
      return 'rejected';
    }
    const sequence = ++executionSequence.current;
    const controller = new AbortController();
    const operation = { sequence, sessionGeneration: origin.sessionGeneration, controller };
    activeOperations.current.set(operationKey, operation);
    const ensureCurrent = () => {
      if (controller.signal.aborted) throw new OffboardingOperationAbortedError();
    };
    const dispatchIfCurrent: Dispatch<AppAction> = (action) => {
      if (activeOperations.current.get(operationKey) === operation) {
        dispatch(action);
      }
    };

    const { step, withdrawToken, withdrawAction, selectedDestinationChain, transferTargetAccountId, withdrawAmountRaw, withdrawAmountSource, convertPlan, convertSlippage } = input;
    const stepAction =
      step === 'withdraw'
        ? withdrawAction
        : step;
    const eventToken = step === 'withdraw'
      ? withdrawToken
      : step === 'convert_negative_usdc' ? convertPlan?.map((item) => item.token).join(',') : undefined;

    try {
      trackEvent('offboarding_step_started', {
        account_id: account.accountId,
        step,
        account_type: account.type,
        action: stepAction,
        token: eventToken,
      });

      setLoadingExecution({ ...origin, step, sequence });
      dispatch({ type: 'START_STEP', step, ...origin });

      if (!auth) {
        trackEvent('offboarding_step_failed', {
          account_id: account.accountId,
          step,
          account_type: account.type,
          action: stepAction,
          token: eventToken,
        });
        dispatchIfCurrent({ type: 'STEP_ERROR', step, error: 'Not authenticated', ...origin });
        return 'error';
      }

      const requiresWalletWithdrawal = step === 'withdraw' && account.type === 'main' && withdrawAction === 'wallet';
      const requiresWalletSignature =
        (step === 'settle_pnl' && account.type === 'main')
        || requiresWalletWithdrawal;
      const withdrawalReceiver = withdrawalReceiverAddress?.trim();

      if (requiresWalletSignature && !signer) {
        trackEvent('offboarding_step_failed', {
          account_id: account.accountId,
          step,
          account_type: account.type,
          action: stepAction,
          token: eventToken,
        });
        dispatchIfCurrent({ type: 'STEP_ERROR', step, error: 'Wallet not connected', ...origin });
        return 'error';
      }

      if (requiresWalletSignature && (!chainId || !chainType)) {
        trackEvent('offboarding_step_failed', {
          account_id: account.accountId,
          step,
          account_type: account.type,
          action: stepAction,
          token: eventToken,
        });
        dispatchIfCurrent({ type: 'STEP_ERROR', step, error: 'Network not detected', ...origin });
        return 'error';
      }

      if (requiresWalletWithdrawal && !withdrawalReceiver) {
        trackEvent('offboarding_step_failed', {
          account_id: account.accountId,
          step,
          account_type: account.type,
          action: stepAction,
          token: withdrawToken,
        });
        dispatchIfCurrent({ type: 'STEP_ERROR', step, error: 'Withdrawal address not detected', ...origin });
        return 'error';
      }

      const brokerId = origin.brokerId;
      const callbacks: Callbacks = {
        onProgress: (message: string) => {
          if (!controller.signal.aborted) dispatchIfCurrent({ type: 'SET_STEP_PROGRESS', message, ...origin });
        },
        shouldContinue: () => !controller.signal.aborted,
      };

      ensureCurrent();
      switch (step) {
        case 'cancel_orders':
          await mergedActions.cancelOrders(auth, account.accountId, callbacks);
          break;
        case 'close_positions':
          await mergedActions.closePositions(account.positions, auth, callbacks);
          break;
        case 'settle_pnl':
          await mergedActions.settlePnl(account.type, account.accountId, brokerId, auth, signer!, chainId!, chainType!, callbacks);
          break;
        case 'convert_negative_usdc':
          if (!convertPlan?.length) {
            throw new Error('No collateral asset is available to convert');
          }
          if (convertPlan.length !== 1) {
            throw new Error('Select one collateral asset to convert');
          }
          if (convertPlan.some((item) => !item.token || !Number.isFinite(item.amount) || item.amount <= 0)) {
            throw new Error('Amount must be greater than 0');
          }
          {
            const convertResult = await mergedActions.convertToUsdc(account.accountId, convertPlan, auth, callbacks, convertSlippage);
            ensureCurrent();
            const convertedItem = convertPlan[0];

            // Settlement could not be confirmed (balance still shows the pre-conversion
            // value). Do NOT apply optimistic state — trusting a stale read here would
            // let the user re-sell collateral for an already-covered debt. Leave the step
            // pending and ask the user to refresh once the balance updates.
            if (!convertResult.settled) {
              dispatchIfCurrent({ type: 'CANCEL_STEP', step, ...origin });
              trackEvent('offboarding_step_unsettled', {
                account_id: account.accountId,
                step,
                account_type: account.type,
                action: stepAction,
                token: eventToken,
                usdc_balance: convertResult.usdcBalance,
              });
              return 'unsettled';
            }

            dispatchIfCurrent({
              type: 'APPLY_CONVERT_TO_USDC_RESULT',
              ...origin,
              token: convertedItem.token,
              collateralHolding: convertResult.collateralHolding,
              usdcBalance: convertResult.usdcBalance,
            });

            if (convertResult.covered) {
              dispatchIfCurrent({ type: 'COMPLETE_STEP', step, ...origin });
              trackEvent('offboarding_step_succeeded', {
                account_id: account.accountId,
                step,
                account_type: account.type,
                action: stepAction,
                token: eventToken,
              });
              return 'success';
            }

            dispatchIfCurrent({ type: 'CANCEL_STEP', step, ...origin });
            trackEvent('offboarding_step_partial', {
              account_id: account.accountId,
              step,
              account_type: account.type,
              action: stepAction,
              token: eventToken,
              usdc_balance: convertResult.usdcBalance,
            });
            return 'partial';
          }
        case 'withdraw': {
          const holding = account.holdings.find((item) => item.token === withdrawToken);
          if (!holding || holding.holding <= 0) {
            throw new Error('Please select an asset to withdraw');
          }

          const availableAmount = Math.min(
            holding.holding - (holding.frozen ?? 0),
            withdrawalAvailableMap?.get(holding.token) ?? Number.POSITIVE_INFINITY,
          );
          if (availableAmount <= 0) {
            throw new Error(`No ${holding.token} is currently withdrawable`);
          }

          const walletWithdrawal = account.type === 'main' && withdrawAction === 'wallet';
          const tokenInfo = tokenInfoMap.get(holding.token);
          if (walletWithdrawal && (!tokenInfo || !isValidTokenDecimals(tokenInfo.decimals))) {
            throw new Error('Token withdrawal metadata is unavailable');
          }
          const rawAmount = withdrawAmountRaw
            ?? (walletWithdrawal ? formatFullWithdrawalAmount(availableAmount, tokenInfo!.decimals) : String(availableAmount));
          let selectedAmount: number | undefined;
          let submittedAt: number | undefined;
          if (!walletWithdrawal) {
            selectedAmount = Number(rawAmount);
            if (!Number.isFinite(selectedAmount) || selectedAmount <= 0) {
              throw new Error('Amount must be greater than 0');
            }
            if (selectedAmount > availableAmount) {
              throw new Error('Amount exceeds available balance');
            }
          }

          if (account.type === 'main' && withdrawAction === 'transfer') {
            if (!transferTargetAccountId.trim()) {
              throw new Error('Please enter a target account ID');
            }

            await mergedActions.internalTransfer(
              account.accountId,
              transferTargetAccountId.trim(),
              holding.token,
              selectedAmount!,
              auth,
              callbacks,
            );
          } else {
            const mainAccount = broker?.accounts.find((item) => item.type === 'main');
            const selectedChainDetail = walletWithdrawal && Array.isArray(tokenInfo?.chain_details)
              ? tokenInfo.chain_details.find((detail) => Number(detail.chain_id) === selectedDestinationChain)
              : undefined;
            if (walletWithdrawal) {
              const walletTokenInfo = tokenInfo!;
              if (!chainType) {
                throw new Error('Wallet not connected');
              }
              if (selectedDestinationChain === null || !selectedChainDetail) {
                throw new Error(`${holding.token} is not supported on the selected destination chain`);
              }
              if ((chainType === 'solana' && selectedDestinationChain !== SOLANA_CHAIN_ID)
                || (chainType === 'evm' && selectedDestinationChain === SOLANA_CHAIN_ID)) {
                throw new Error('Destination chain does not match the connected wallet type');
              }
              const { normalizedRawAmount } = parseAtomicWithdrawalAmount(rawAmount, walletTokenInfo.decimals);
              if (isWithdrawalAmountAboveAvailable(rawAmount, availableAmount, walletTokenInfo.decimals)) {
                throw new Error('Amount exceeds available balance');
              }
              const minimumError = getWalletWithdrawalAmountError(
                rawAmount,
                walletTokenInfo.decimals,
                walletTokenInfo.minimum_withdraw_amount,
                selectedChainDetail.withdrawal_fee,
              );
              if (minimumError) throw new Error(minimumError);
              selectedAmount = Number(normalizedRawAmount);
            }
            submittedAt = walletWithdrawal ? Date.now() : undefined;
            await mergedActions.withdrawOrTransfer(
              account,
              holding.token,
              rawAmount,
              mainAccount?.accountId ?? '',
              brokerId,
              auth,
              signer,
              chainId,
              chainType,
              selectedDestinationChain ?? undefined,
              withdrawalReceiver,
              callbacks,
              tokenInfo?.decimals,
            );
            ensureCurrent();
          }

          if (walletWithdrawal) {
            const autoFullWithdrawal = withdrawAmountSource === 'auto' || withdrawAmountRaw === undefined;
            const remainingAvailable = availableAmount - selectedAmount!;
            const expectedHolding = autoFullWithdrawal
              && isSubAtomicWithdrawalRemainder(remainingAvailable, tokenInfo!.decimals)
              ? Math.max(0, holding.holding - availableAmount)
              : Math.max(0, holding.holding - selectedAmount!);
            dispatchIfCurrent({
              type: 'WALLET_WITHDRAWAL_SUBMITTED',
              token: holding.token,
              expectedHolding,
              submittedAt: submittedAt!,
              ...(autoFullWithdrawal ? { fullWithdrawalDecimals: tokenInfo!.decimals } : {}),
              requestBarrierId: createAccountDataRequestBarrier(),
              ...origin,
            });
          } else {
            dispatchIfCurrent({
              type: 'WITHDRAW_ASSET',
              token: holding.token,
              amount: selectedAmount!,
              requestBarrierId: createAccountDataRequestBarrier(),
              ...origin,
            });
          }
          break;
        }
      }

      ensureCurrent();
      if (step !== 'withdraw') {
        dispatchIfCurrent({ type: 'COMPLETE_STEP', step, ...origin });
      }

      trackEvent('offboarding_step_succeeded', {
        account_id: account.accountId,
        step,
        account_type: account.type,
        action: stepAction,
        token: eventToken,
      });

      if (activeOperations.current.get(operationKey) === operation && !requiresWalletWithdrawal) {
        onStepComplete?.();
      }
      return 'success';
    } catch (error: unknown) {
      if (error instanceof OffboardingOperationAbortedError) {
        dispatchIfCurrent({ type: 'CANCEL_STEP', step, ...origin });
        return 'error';
      }
      const code = error && typeof error === 'object' && 'code' in error ? error.code : undefined;
      if (code === 'ACTION_REJECTED' || code === '4001') {
        trackEvent('offboarding_step_rejected', {
          account_id: account.accountId,
          step,
          account_type: account.type,
          action: stepAction,
          token: eventToken,
        });
        dispatchIfCurrent({ type: 'CANCEL_STEP', step, ...origin });
        return 'rejected';
      } else {
        trackEvent('offboarding_step_failed', {
          account_id: account.accountId,
          step,
          account_type: account.type,
          action: stepAction,
          token: eventToken,
        });
        captureException(error, {
          tags: {
            feature: 'offboarding',
            step,
            account_type: account.type,
            action: stepAction,
          },
          extra: {
            account_id: account.accountId,
            broker_id: broker?.id,
            token: eventToken,
            chain_type: chainType,
            chain_id: chainId,
          },
        });
        const errorMessage = step === 'withdraw'
          ? getWithdrawalErrorMessage(error, account, withdrawToken)
          : error instanceof Error ? error.message : 'Step failed';
        dispatchIfCurrent({ type: 'STEP_ERROR', step, error: errorMessage, ...origin });
        return 'error';
      }
    } finally {
      setLoadingExecution((current) => current?.sequence === sequence ? null : current);
      if (activeOperations.current.get(operationKey) === operation) {
        activeOperations.current.delete(operationKey);
      }
    }
  }

  return {
    loadingStep: loadingExecution
      && loadingExecution.sessionGeneration === sessionGeneration
      && loadingExecution.brokerId === broker?.id
      && loadingExecution.accountId === account?.accountId
      ? loadingExecution.step
      : null,
    executeStep,
  };
}
